import React, { useCallback, useEffect, forwardRef, useState } from "react";
import {
  styled,
  TextField,
  //Radio,
  //FormControlLabel,
  CircularProgress,
  IconButton,
  Button,
} from "@mui/material";
//import DefaultButton from "./UI/DefaultButton";
import {
  FeedbackInput,
  FeedbackStates,
} from "fe-web-components/feedback/types";
import { ReactComponent as CloseIcon } from "fe-web-components/assets/close.svg";
import { useTranslateByGender } from "fe-common/locale/useTranslateByGender";

//region [[ Styles ]]
const FeedbackFormView = styled("div")(({ theme }) => ({
  background: "#FFFFFF",
  borderRadius: 8,
  padding: "22px 20px",
  fontSize: 14,
  position: "relative",
  [theme.breakpoints.down("sm")]: {
    marginLeft: -44,
  },
}));
const FeedbackTitle = styled("div")({
  marginBottom: 14,
});
// const FeedbackButtons = styled("div")(({ theme }) => ({
//   display: "flex",
//   flexDirection: "row",
//   flexWrap: "wrap",
//   gap: 14,
//   margin: "14px 0",
//   [theme.breakpoints.down("sm")]: {
//     margin: "8px 0 14px 0",
//     gap: 8,
//   },
// }));
// const FeedbackButton = styled(FormControlLabel)(({ theme }) => ({
//   flex: "1 1 calc(50% - 7px)",
//   width: 220,
//   maxWidth: "100%",
//   color: theme.palette.text.primary + "99",
//   margin: 0,
//   fontSize: 14,
//   padding: 20,
//   border: `1px solid ${theme.palette.divider}`,
//   borderRadius: 8,
//   gap: 8,
//   "& span": {
//     fontSize: 14,
//     padding: 0,
//   },
//   "&:hover": {
//     color: theme.palette.text.primary,
//     background: theme.palette.primary.light,
//   },
//   "&.selected": {
//     color: theme.palette.text.primary,
//     background: theme.palette.primary.light,
//   },
//   [theme.breakpoints.down("sm")]: {
//     padding: 16,
//     flex: "1 1 100%",
//   },
// }));
const FeedbackFormFooter = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 14,
  marginTop: 14,
  [theme.breakpoints.down("sm")]: {
    marginTop: 8,
  },
}));
const SkipButton = styled("button")(({ theme }) => ({
  textTransform: "none",
  textDecoration: "underline",
  padding: "0 10px",
  color: theme.palette.text.primary,
  opacity: 0.6,
  border: "none",
  background: "transparent",
  cursor: "pointer",
  "&:hover": {
    opacity: 1,
  },
}));
const CloseButton = styled(IconButton)({
  position: "absolute",
  right: 10,
  top: 10,
  "& svg": {
    width: 12,
    height: 12,
  },
  "&:dir(rtl)": {
    right: "auto",
    left: 10,
  },
});

//endregion [[ Styles ]]

//region [[ Props ]]
export interface FeedbackFormProps {
  feedbackState: FeedbackStates;
  onSend: (
    feedbackInput: FeedbackInput,
    showThankYouMessage: boolean,
  ) => Promise<boolean>;
  onClose: () => void;
  rtlDisplay?: boolean;
}

// const feedbackOptions: { [key in FeedbackStates]?: string[] } = {
//   [FeedbackStates.none]: [],
//   [FeedbackStates.positive]: [
//     "Clinically accurate",
//     "On the mark",
//     "Coherent",
//     "High-value references",
//   ],
//   [FeedbackStates.negative]: [
//     "Clinically inaccurate",
//     "Incoherent",
//     "Too wordy",
//     "Low-quality references",
//   ],
// };

//endregion [[ Props ]]

//region [[ Functions ]]

//endregion [[ Functions ]]

export const FeedbackForm = forwardRef(
  (
    { feedbackState, onSend, onClose, rtlDisplay }: FeedbackFormProps,
    ref: React.ForwardedRef<any>,
  ) => {
    const [freeText, setFreeText] = useState<string>("");
    const [predefinedText, setPredefinedText] = useState<string[]>([]);
    const [sendingFeedback, setSendingFeedback] = useState<boolean>(false);
    //const { sendFeedback, sessionInfo } = useSessionStore();
    const { translate } = useTranslateByGender();

    useEffect(() => {
      setPredefinedText([]);
    }, [feedbackState]);

    const handleSendFeedback = useCallback(
      async (fText?: string, pText?: string, showThankYouMessage?: boolean) => {
        setSendingFeedback(true);
        await onSend(
          {
            freeText: fText,
            positive: feedbackState === FeedbackStates.positive,
            predefinedText: pText,
          },
          showThankYouMessage,
        );
        setSendingFeedback(false);
      },
      [feedbackState, onSend],
    );

    const handleSubmit = useCallback(
      () => handleSendFeedback(freeText, predefinedText.join(", "), true),
      [freeText, predefinedText, handleSendFeedback],
    );

    const handleSkip = useCallback(() => onClose(), [onClose]);

    // const togglePredefinedText = useCallback((text: string) => {
    //   setPredefinedText((prevState) =>
    //     prevState.includes(text)
    //       ? prevState.filter((t) => t !== text)
    //       : [...prevState, text],
    //   );
    // }, []);

    return (
      <FeedbackFormView ref={ref} dir={rtlDisplay ? "rtl" : ""}>
        <CloseButton
          size={"small"}
          onClick={onClose}
          title={translate("CLOSE_TEXT")}
        >
          <CloseIcon />
        </CloseButton>
        <FeedbackTitle>
          {translate(
            feedbackState === FeedbackStates.positive
              ? "DIALOGUE_FEEDBACK_POSITIVE_TITLE"
              : "DIALOGUE_FEEDBACK_NEGATIVE_TITLE",
          )}
        </FeedbackTitle>
        <TextField
          value={freeText}
          fullWidth
          multiline
          rows={3}
          variant={"outlined"}
          onChange={(e) => setFreeText(e.target.value)}
        />
        {/*<FeedbackButtons>*/}
        {/*  {feedbackOptions[feedbackState].map((option, i) => (*/}
        {/*    <FeedbackButton*/}
        {/*      control={*/}
        {/*        <Radio*/}
        {/*          size={"small"}*/}
        {/*          checked={predefinedText.includes(option)}*/}
        {/*          onClick={() => togglePredefinedText(option)}*/}
        {/*        />*/}
        {/*      }*/}
        {/*      label={option}*/}
        {/*      key={"fOpt" + i}*/}
        {/*      className={predefinedText.includes(option) ? "selected" : ""}*/}
        {/*    />*/}
        {/*  ))}*/}
        {/*</FeedbackButtons>*/}
        <FeedbackFormFooter>
          <SkipButton onClick={handleSkip} disabled={sendingFeedback}>
            {translate("SKIP")}
          </SkipButton>
          <Button
            variant={"outlined"}
            onClick={handleSubmit}
            disabled={sendingFeedback}
            endIcon={sendingFeedback ? <CircularProgress size={14} /> : null}
          >
            {translate("SUBMIT")}
          </Button>
        </FeedbackFormFooter>
      </FeedbackFormView>
    );
  },
);
